import React, { useEffect, useState } from 'react';
import { ServiceProviderType, UpdateServiceProviderTypeRequest } from '../models/ServiceProviderType';

interface EditServiceProviderTypeModalProps {
  isOpen: boolean;
  serviceType: ServiceProviderType | null;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateServiceProviderTypeRequest) => Promise<void>;
}

export const EditServiceProviderTypeModal: React.FC<EditServiceProviderTypeModalProps> = ({
  isOpen,
  serviceType,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [form, setForm] = useState<UpdateServiceProviderTypeRequest>({ tag: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen && serviceType) {
      setForm({
        tag: serviceType.tag,
        name: serviceType.name,
        description: serviceType.description,
        icon: serviceType.icon || '',
        imageUrl: serviceType.imageUrl,
        startingPrice: serviceType.startingPrice,
        currency: serviceType.currency || 'USD',
      });
      setErrors({});
    }
  }, [isOpen, serviceType]);

  if (!isOpen || !serviceType) return null;

  const setField = (field: keyof UpdateServiceProviderTypeRequest, value: string | number | null) => {
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => ({ ...current, [field]: '' }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const nextErrors: Record<string, string> = {};

    if (!form.name?.trim()) nextErrors.name = 'Name is required';
    if (!form.description?.trim()) nextErrors.description = 'Description is required';
    if (!form.icon?.trim()) nextErrors.icon = 'Icon is required';
    if (!form.currency || !/^[A-Z]{3}$/.test(form.currency)) nextErrors.currency = 'Currency must be a 3-letter ISO code';
    if (form.startingPrice !== null && form.startingPrice !== undefined && form.startingPrice < 0) {
      nextErrors.startingPrice = 'Starting price cannot be negative';
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    await onSubmit({
      ...form,
      name: form.name?.trim(),
      description: form.description?.trim(),
      icon: form.icon?.trim(),
      imageUrl: form.imageUrl || null,
      currency: form.currency?.toUpperCase(),
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-gray-900/50 p-4">
      <div className="max-h-[95vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Edit Service Type</h2>
            <p className="text-xs text-gray-500">Tag: {serviceType.tag}</p>
          </div>
          <button type="button" onClick={onClose} disabled={loading} className="text-gray-400 hover:text-gray-700" aria-label="Close">✕</button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm font-medium text-gray-700">Name *
            <input value={form.name || ''} onChange={event => setField('name', event.target.value)} className={`mt-1 w-full rounded-md border px-3 py-2 ${errors.name ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.name && <span className="mt-1 block text-xs text-red-600">{errors.name}</span>}
          </label>
          <label className="block text-sm font-medium text-gray-700">Description *
            <textarea value={form.description || ''} onChange={event => setField('description', event.target.value)} rows={3} className={`mt-1 w-full rounded-md border px-3 py-2 ${errors.description ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.description && <span className="mt-1 block text-xs text-red-600">{errors.description}</span>}
          </label>
          <label className="block text-sm font-medium text-gray-700">Icon *
            <input value={form.icon || ''} onChange={event => setField('icon', event.target.value)} className={`mt-1 w-full rounded-md border px-3 py-2 ${errors.icon ? 'border-red-400' : 'border-gray-300'}`} />
            {errors.icon && <span className="mt-1 block text-xs text-red-600">{errors.icon}</span>}
          </label>
          <label className="block text-sm font-medium text-gray-700">Service Image URL
            <input type="url" value={form.imageUrl || ''} onChange={event => setField('imageUrl', event.target.value || null)} className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" placeholder="https://example.com/services/image.jpg" />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="text-sm font-medium text-gray-700">Starting Price
              <input type="number" min={0} step="0.01" value={form.startingPrice ?? ''} onChange={event => setField('startingPrice', event.target.value === '' ? null : Number(event.target.value))} className={`mt-1 w-full rounded-md border px-3 py-2 ${errors.startingPrice ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.startingPrice && <span className="mt-1 block text-xs text-red-600">{errors.startingPrice}</span>}
            </label>
            <label className="text-sm font-medium text-gray-700">Currency *
              <input maxLength={3} value={form.currency || ''} onChange={event => setField('currency', event.target.value.toUpperCase())} className={`mt-1 w-full rounded-md border px-3 py-2 ${errors.currency ? 'border-red-400' : 'border-gray-300'}`} />
              {errors.currency && <span className="mt-1 block text-xs text-red-600">{errors.currency}</span>}
            </label>
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
            <button type="button" onClick={onClose} disabled={loading} className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700">Cancel</button>
            <button type="submit" disabled={loading} className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">{loading ? 'Updating...' : 'Update Service Type'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};
